import { Injectable } from '@angular/core';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, take, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { DigitalesService } from './digitales.service';
import { gpcGoogleService } from './gpcGoogle.service';
import { HistorialCambiosService } from './historial-cambios.service';
import { VersionesDocumentoService } from './versiones-documento.service';

const Swal = require("sweetalert2");

export interface DocumentoFormData {
  codigo: string;
  nombre_documento: number | null;
  tipo_documental: number | null;
  pilar: number | null;
  estado: number | null;
  retencion: number | null;
  observaciones: string | null;
}

export interface RutaVersion {
  nit: string;
  codigo: string;
  nombreDocumento: string;
  numeroVersion: string | number;
}

export interface VistaArchivo {
  url: string;
  formato: 'pdf' | 'excel' | 'word';
}

@Injectable({
  providedIn: 'root'
})
export class ImplementacionService {
    // Obtener implementación y todas sus versiones por ID de maestro
    getImplementacionConVersionesById(id: number): Observable<any> {
      return this.http.get<any>(`${this.apiUrl}/${id}/con-versiones`);
    }
  private apiUrl = `${environment.apiUrl}/api/implementacion-pesv`;
  private readonly urlsPorCarpeta = new Map<string, string>();

  constructor(
    private http: HttpClient,
    private digitales: DigitalesService,
    private gpcGoogle: gpcGoogleService,
    private versiones: VersionesDocumentoService,
    private historial: HistorialCambiosService
  ) { }

  getDocumento(id: number): Observable<any> {
    return this.getImplementacionById(id);
  }

  getVersiones(documentoId: number): Observable<any[]> {
    return this.versiones.getVersionesByDocumento(documentoId);
  }

  getHistorial(documentoId: number): Observable<any[]> {
    return this.historial.getHistorialByDocumento(documentoId);
  }

  guardarDocumento(id: number | null, formulario: DocumentoFormData): Observable<any> {
    const payload = this.construirPayloadDocumento(formulario);
    return id
      ? this.updateImplementacion(id, payload)
      : this.createImplementacion(payload);
  }

  guardarVersion(payload: any): Observable<any> {
    return this.versiones.createVersion(payload);
  }

  actualizarVersion(id: number, cambios: any): Observable<any> {
    return this.versiones.updateVersion(id, cambios);
  }

  marcarVersionVigente(id: number): Observable<any> {
    return this.versiones.setVersionVigente(id);
  }

  crearFormularioVacio(): DocumentoFormData {
    return {
      codigo: '',
      nombre_documento: null,
      tipo_documental: null,
      pilar: null,
      estado: null,
      retencion: null,
      observaciones: null
    };
  }

  private obtenerId(valor: any): number | null {
    const id = typeof valor === 'object' && valor !== null ? valor.id : valor;
    const numero = Number(id);
    return Number.isInteger(numero) && numero > 0 ? numero : null;
  }

  formularioDesdeDocumento(documento: any): DocumentoFormData {
    return {
      codigo: documento?.codigo ?? '',
      nombre_documento: this.obtenerId(documento?.nombre_id ?? documento?.nombre),
      tipo_documental: this.obtenerId(documento?.tipo_documental),
      pilar: this.obtenerId(documento?.pilar),
      estado: this.obtenerId(documento?.estado),
      retencion: documento?.retencion ?? null,
      observaciones: documento?.observaciones ?? null
    };
  }

  validarDocumento(formulario: DocumentoFormData): { [campo: string]: string } {
    const errores: { [campo: string]: string } = {};
    const retencion: any = formulario.retencion;

    if (!formulario.codigo || !formulario.codigo.trim()) {
      errores['codigo'] = 'El código es requerido';
    }
    if (!formulario.nombre_documento) {
      errores['nombre_documento'] = 'Seleccione el nombre del documento';
    }
    if (!formulario.tipo_documental) {
      errores['tipo_documental'] = 'Seleccione el tipo documental';
    }
    if (!formulario.estado) {
      errores['estado'] = 'Seleccione el estado';
    }
    if (
      retencion === null ||
      retencion === undefined ||
      retencion === '' ||
      !Number.isInteger(Number(retencion)) ||
      Number(retencion) < 0
    ) {
      errores['retencion'] = 'Ingrese los años de retención (número entero, 0 o más)';
    }
    return errores;
  }

  private construirPayloadDocumento(formulario: DocumentoFormData): any {
    const retencion: any = formulario.retencion;
    return {
      codigo: (formulario.codigo || '').trim(),
      nombre: formulario.nombre_documento,
      tipo_documental: formulario.tipo_documental,
      pilar: formulario.pilar,
      estado: formulario.estado,
      retencion: retencion === null || retencion === undefined || retencion === '' ? null : Number(retencion),
      observaciones: formulario.observaciones
    };
  }

  fechaLocalISO(): string {
    const fecha = new Date();
    const mes = String(fecha.getMonth() + 1).padStart(2, '0');
    const dia = String(fecha.getDate()).padStart(2, '0');
    return `${fecha.getFullYear()}-${mes}-${dia}`;
  }

  normalizarVersion(valor: any): string {
    return this.normalizarSegmentoRuta(valor).replace(/^v+(?=\d)/, '');
  }

  private normalizarSegmentoRuta(valor: any): string {
    return (valor ?? '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9_-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();
  }

  private construirCarpetaVersion(ruta: RutaVersion): string {
    return [
      String(ruta.nit).trim(),
      'implementacion',
      this.normalizarSegmentoRuta(ruta.codigo),
      this.normalizarSegmentoRuta(ruta.nombreDocumento) || 'documentos',
      `v${this.normalizarVersion(ruta.numeroVersion)}`
    ].join('/');
  }

  private construirNombreArchivo(archivo: File): string {
    const ultimoPunto = archivo.name.lastIndexOf('.');
    const base = ultimoPunto > 0 ? archivo.name.substring(0, ultimoPunto) : archivo.name;
    const extension = ultimoPunto > 0 ? archivo.name.substring(ultimoPunto + 1) : '';
    const nombreBase = this.normalizarSegmentoRuta(base) || 'documento';
    const extensionNormalizada = this.normalizarSegmentoRuta(extension);
    return extensionNormalizada ? `${nombreBase}.${extensionNormalizada}` : nombreBase;
  }

  construirRutaArchivo(ruta: RutaVersion, archivo: File): string {
    return `${this.construirCarpetaVersion(ruta)}/${this.construirNombreArchivo(archivo)}`;
  }

  subir(ruta: RutaVersion, archivo: File): Observable<string> {
    if (!this.obtenerFormatoArchivo(archivo.name)) {
      return throwError(() => new Error('FORMATO_NO_SOPORTADO'));
    }

    const carpeta = this.construirCarpetaVersion(ruta);
    return this.digitales
      .subirArchivo(archivo, 0, carpeta, this.construirNombreArchivo(archivo), 'DOC')
      .pipe(
        filter(evento => evento.type === HttpEventType.Response),
        take(1),
        map((evento: any) => {
          const url = evento?.body?.url;
          if (!url) {
            throw new Error('El servidor no devolvió la URL del archivo subido');
          }
          this.urlsPorCarpeta.set(carpeta, url);
          return url as string;
        })
      );
  }

  obtenerArchivoVisualizacion(ruta: RutaVersion, urlRespaldo?: string | null): Observable<VistaArchivo> {
    if (urlRespaldo) {
      const formato = this.obtenerFormatoArchivo(urlRespaldo);
      if (!formato) {
        return throwError(() => new Error('FORMATO_NO_SOPORTADO'));
      }
      return this.obtenerUrlFirmada(urlRespaldo).pipe(
        map(url => ({ url, formato })),
        catchError(() => /^https?:\/\//i.test(urlRespaldo)
          ? of({ url: urlRespaldo, formato })
          : throwError(() => new Error('No se pudo resolver la URL guardada del archivo')))
      );
    }

    const carpeta = this.construirCarpetaVersion(ruta);
    const urlSubida = this.urlsPorCarpeta.get(carpeta);
    if (urlSubida) {
      return this.obtenerArchivoVisualizacion(ruta, urlSubida);
    }

    return this.listarArchivoEnCarpeta(carpeta).pipe(
      mergeMap((archivo: any) => {
        const rutaArchivo = this.rutaArchivoListado(archivo, carpeta);
        const formato = this.obtenerFormatoArchivo(rutaArchivo) ||
          this.obtenerFormatoArchivo(archivo?.contentType || archivo?.mimeType || '');
        if (!formato) {
          return throwError(() => new Error('FORMATO_NO_SOPORTADO'));
        }
        return this.obtenerUrlFirmada(rutaArchivo).pipe(map(url => ({ url, formato })));
      })
    );
  }

  descargar(ruta: RutaVersion, urlRespaldo?: string | null): Observable<string> {
    return this.obtenerArchivoVisualizacion(ruta, urlRespaldo).pipe(
      mergeMap((vista: VistaArchivo) => this.descargarUrl(vista.url, ruta))
    );
  }

  descargarUrl(urlArchivo: string, ruta?: RutaVersion): Observable<string> {
    return from(fetch(urlArchivo)).pipe(
      mergeMap((respuesta: Response) => {
        if (!respuesta.ok) {
          return throwError(() => new Error(`Error al descargar archivo: ${respuesta.status}`));
        }
        return from(respuesta.blob()).pipe(
          map((blob: Blob) => ({
            blob,
            nombre: ruta
              ? this.nombreDesdeUrl(urlArchivo, ruta, blob)
              : this.rutaDesdeUrl(urlArchivo)?.split('/').pop() || 'documento'
          }))
        );
      }),
      map(({ blob, nombre }: { blob: Blob; nombre: string }) => {
        const urlTemporal = window.URL.createObjectURL(blob);
        const enlace = document.createElement('a');
        enlace.href = urlTemporal;
        enlace.download = nombre;
        enlace.style.display = 'none';
        document.body.appendChild(enlace);
        enlace.click();
        enlace.remove();
        setTimeout(() => window.URL.revokeObjectURL(urlTemporal), 1000);
        return nombre;
      })
    );
  }

  private listarArchivoEnCarpeta(carpeta: string): Observable<any> {
    return this.gpcGoogle.listarArchivosPorRuta(carpeta).pipe(
      map((respuesta: any) => {
        const archivos = Array.isArray(respuesta)
          ? respuesta
          : Array.isArray(respuesta?.archivos)
            ? respuesta.archivos
            : Array.isArray(respuesta?.documentos)
              ? respuesta.documentos
              : Array.isArray(respuesta?.data)
                ? respuesta.data
                : [];
        const archivo = archivos.find((item: any) => {
          const ruta = item?.rutaCompleta ||
            item?.ruta_completa ||
            item?.path ||
            item?.ruta ||
            item?.name ||
            item?.nombre ||
            item?.archivo ||
            item?.fileName ||
            item?.filename ||
            item?.url;
          return (typeof ruta === 'string' && this.obtenerFormatoArchivo(ruta)) ||
            this.obtenerFormatoArchivo(item?.contentType || item?.mimeType || '');
        });
        if (!archivo) {
          throw new Error(`No se encontró un archivo PDF, Excel o Word en ${carpeta}`);
        }
        return archivo;
      })
    );
  }

  private rutaArchivoListado(archivo: any, carpeta: string): string {
    const ruta = archivo?.rutaCompleta ||
      archivo?.ruta_completa ||
      archivo?.path ||
      archivo?.ruta ||
      archivo?.name ||
      archivo?.nombre ||
      archivo?.archivo ||
      archivo?.fileName ||
      archivo?.filename ||
      archivo?.url;

    if (typeof ruta !== 'string' || !ruta.trim()) {
      throw new Error(`La respuesta de archivos no incluye una ruta válida para ${carpeta}`);
    }
    if (/^https?:\/\//i.test(ruta)) {
      return ruta;
    }
    const rutaLimpia = ruta.replace(/^\/+/, '');
    return rutaLimpia.startsWith(`${carpeta}/`) ? rutaLimpia : `${carpeta}/${rutaLimpia}`;
  }

  private obtenerUrlFirmada(rutaOUrl: string): Observable<string> {
    const ruta = this.rutaDesdeUrl(rutaOUrl);
    if (!ruta) {
      return throwError(() => new Error('La ruta del archivo no es válida'));
    }
    return this.gpcGoogle.getSignedUrlFiles(ruta).pipe(
      map((respuesta: any) => {
        if (!respuesta?.url) {
          throw new Error('El servidor no devolvió una URL firmada');
        }
        return respuesta.url as string;
      })
    );
  }

  private rutaDesdeUrl(valor: string): string | null {
    try {
      const entrada = String(valor || '').trim();
      if (!entrada) {
        return null;
      }
      const segmentos = /^https?:\/\//i.test(entrada)
        ? decodeURIComponent(new URL(entrada).pathname).replace(/^\/+/, '').split('/').filter(Boolean)
        : entrada.replace(/^\/+/, '').split('/').filter(Boolean);
      if (segmentos[0] === 'admin-digitales') {
        segmentos.shift();
      }
      return segmentos.join('/') || null;
    } catch (error) {
      return null;
    }
  }

  private nombreDesdeBlob(blob: Blob, ruta: RutaVersion): string {
    const extension = this.extensionPorMime(blob.type);
    return `${this.normalizarSegmentoRuta(ruta.codigo) || 'documento'}_v${this.normalizarVersion(ruta.numeroVersion)}.${extension}`;
  }

  obtenerFormatoArchivo(valor: string | File): 'pdf' | 'excel' | 'word' | null {
    const nombre = typeof valor === 'string' ? valor : valor.name;
    const mime = typeof valor === 'string'
      ? (/^[\w.+-]+\/[\w.+-]+$/.test(valor.trim()) ? valor.toLowerCase() : '')
      : valor.type.toLowerCase();
    const extension = nombre
      .split(/[?#]/)[0]
      .split('/')
      .pop()
      ?.split('.')
      .pop()
      ?.toLowerCase();

    if (extension === 'pdf' || mime.includes('application/pdf')) return 'pdf';
    if (
      extension === 'xls' ||
      extension === 'xlsx' ||
      mime.includes('application/vnd.ms-excel') ||
      mime.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    ) return 'excel';
    if (
      extension === 'doc' ||
      extension === 'docx' ||
      mime.includes('application/msword') ||
      mime.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document')
    ) return 'word';
    return null;
  }

  private nombreDesdeUrl(urlArchivo: string, ruta: RutaVersion, blob: Blob): string {
    const rutaArchivo = this.rutaDesdeUrl(urlArchivo);
    const nombre = rutaArchivo?.split('/').pop();
    if (nombre) {
      try {
        return decodeURIComponent(nombre);
      } catch (error) {
        return nombre;
      }
    }
    return this.nombreDesdeBlob(blob, ruta);
  }

  private extensionPorMime(mime: string): string {
    const tipo = (mime || '').toLowerCase();
    if (tipo.includes('pdf')) return 'pdf';
    if (tipo.includes('wordprocessingml') || tipo.includes('msword')) return 'docx';
    if (tipo.includes('spreadsheetml') || tipo.includes('excel')) return 'xlsx';
    if (tipo.includes('presentationml') || tipo.includes('powerpoint')) return 'pptx';
    if (tipo.startsWith('image/')) return tipo.split('/')[1].split(';')[0] || 'bin';
    if (tipo.startsWith('text/')) return 'txt';
    return 'bin';
  }

  /**
   * Extraer mensaje del error del backend
   */
  private extraerMensajeError(error: any): string {
    if (error?.error?.error) {
      return error.error.error;
    } else if (error?.error?.message) {
      return error.error.message;
    } else if (error?.error?.detalle) {
      return error.error.detalle;
    } else if (typeof error?.error === 'string') {
      return error.error;
    }
    return 'Error inesperado en el servidor';
  }

  /**
   * Mostrar error desde el backend
   */
  private mostrarError(titulo: string, error: any): void {
    const mensaje = this.extraerMensajeError(error);
    Swal.fire({
      icon: 'error',
      title: titulo,
      text: mensaje,
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#3085d6'
    });
  }

  /**
   * Mostrar éxito
   */
  private mostrarExito(titulo: string, texto: string): void {
    Swal.fire({
      icon: 'success',
      title: titulo,
      text: texto,
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#3085d6'
    });
  }


  private normalizarPayload(data: any): any {
    if (data && data.estado !== undefined && data.estado !== null) {
      const estadoNumero = Number(data.estado);
      const estadoFinal = Number.isNaN(estadoNumero)
        ? data.estado
        : estadoNumero === 0
        ? 4
        : estadoNumero;

      return {
        ...data,
        estado: estadoFinal,
      };
    }
    return data;
  }

  // Crear una nueva implementación PESV
  createImplementacion(data: any): Observable<any> {
    return this.http.post<any>(this.apiUrl, this.normalizarPayload(data));
  }

  // Obtener todas las implementaciones PESV (con opción de incluir obsoletos)
  getAllImplementaciones(incluirObsoletos: boolean = false): Observable<any[]> {
    return this.http.get<any[]>(this.apiUrl);
  }

  // Obtener una implementación PESV por ID
  getImplementacionById(id: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/${id}`);
  }

  // Actualizar una implementación PESV por ID
  updateImplementacion(id: number, data: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${id}`, this.normalizarPayload(data));
  }

  // Inactivar una implementación PESV por ID
  inactivateImplementacion(id: number): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${id}/inactivate`, null);
  }

  // Activar una implementación PESV por ID
  activateImplementacion(id: number): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${id}/activate`, null);
  }

  // Eliminar una implementación PESV por ID
  deleteImplementacion(id: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/${id}`).pipe(
      tap((res: any) => {
        this.mostrarExito('Éxito', 'Documento eliminado correctamente');
      }),
      catchError((err: any) => {
        this.mostrarError('Error al eliminar', err);
        return throwError(() => err);
      })
    );
  }

  // 📅 Calcular vencimiento de un documento (fecha vencimiento y si está vencido)
  calcularVencimiento(documentoId: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/${documentoId}/vencimiento`).pipe(
      catchError((err: any) => {
        console.error('Error al calcular vencimiento:', err);
        return throwError(() => err);
      })
    );
  }

  // 📚 Obtener historial de versiones por código de documento
  getHistorialByCodigo(codigo: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/historial/${codigo}`).pipe(
      catchError((err: any) => {
        console.error('Error al obtener historial:', err);
        return throwError(() => err);
      })
    );
  }
}